import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, ExternalLink, QrCode, Share2 } from "lucide-react";
import type { Paginated, UserDto } from "@gatiq/shared";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  Modal,
  Pagination,
  SearchInput,
  Skeleton,
  useToast,
} from "@/ui";
import { useAuth } from "@/auth/AuthProvider";
import { useCan } from "@/auth/access";
import { api, ApiClientError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { QrPosterPanel } from "@/components/QrPosterPanel";

/**
 * Every URL an org admin needs to hand out, in one place.
 *
 * These links get sent over WhatsApp, printed on posters and read aloud down
 * the phone, so the job of this screen is to make them easy to copy correctly —
 * not to look at. Each row shows the full absolute URL, because a relative
 * path is useless once it has been pasted into a message.
 */

interface LinkRow {
  label: string;
  description?: string;
  path: string;
  /** Already absolute and off-site — a counter's WhatsApp registration link (D-22). */
  url?: string;
  audience: string;
}

/** A counter row from `/org/counters`; `registrationUrl` is null while no WhatsApp is linked. */
type CounterRow = UserDto & { registrationUrl?: string | null };

export function OrgLinksPage() {
  const { user } = useAuth();
  const toast = useToast();

  const orgSlug = user?.orgSlug ?? "";
  // Counter links are a list of counters, so they follow the counters grant (own chain or whole org).
  const seeCounters = Boolean(useCan()("counters"));
  const [counters, setCounters] = useState<CounterRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [posterCounter, setPosterCounter] = useState<CounterRow | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    if (!seeCounters) return;
    const request = ++requestSeq.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: "100" });
      if (query) params.set("q", query);
      const res = await api.get<Paginated<CounterRow>>(`/org/counters?${params}`);
      if (request !== requestSeq.current) return;
      setCounters(res.items);
      setTotal(res.total);
      setTotalPages(res.totalPages);
      setError(null);
    } catch (err) {
      if (request !== requestSeq.current) return;
      setError(err instanceof ApiClientError ? err.message : "Could not load counters.");
    } finally {
      if (request === requestSeq.current) setLoading(false);
    }
  }, [seeCounters, page, query]);

  useEffect(() => {
    void load();
    return () => { requestSeq.current++; };
  }, [load]);

  const portalLinks: LinkRow[] = [
    {
      label: "Staff sign in",
      path: `/${orgSlug}/login`,
      audience: "Sales reps · Counters",
    },
    {
      label: "Karigar sign in",
      path: `/${orgSlug}/login/karigar`,
      audience: "Karigars",
    },
    {
      label: "Forgot password",
      path: `/${orgSlug}/forgot-password`,
      audience: "Sales reps · Counters",
    },
  ];

  return (
    <Page>
      <PageHeader title="Links" />

      <PageBody>
        <Card className="mb-5">
          <CardHeader title="Portal links" />
          <CardBody className="flex flex-col gap-3">
            {portalLinks.map((link) => (
              <LinkItem key={link.path} link={link} onToast={toast} />
            ))}
          </CardBody>
        </Card>

        {seeCounters ? (
        <Card>
          <CardHeader title="Karigar registration links" action={
            <SearchInput label="Search counters" placeholder="Search counters…" value={search}
              onChange={(value) => { setSearch(value); setPage(1); }} />
          } />

          {error ? (
            <CardBody>
              <Alert kind="danger">{error}</Alert>
            </CardBody>
          ) : loading ? (
            <CardBody className="flex flex-col gap-3">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </CardBody>
          ) : !counters || counters.length === 0 ? (
            <EmptyState
              message={query ? "Try another counter name or number." : "Add a counter and its registration link will appear here, ready to print or share."}
              title={query ? "No matching counters" : "No counters yet"}
            />
          ) : counters.every((counter) => !counter.registrationUrl) ? (
            <CardBody>
              <Alert kind="danger">
                WhatsApp is not connected, so registration links cannot be made yet. Ask the platform admin to link WhatsApp.
              </Alert>
            </CardBody>
          ) : (
            <CardBody className="flex flex-col gap-3">
              {counters.map((counter) =>
                counter.registrationUrl ? (
                  <LinkItem
                    key={counter.id}
                    link={{
                      label: counter.name,
                      description: counter.salesRepName
                        ? `Sales rep: ${counter.salesRepName}`
                        : "No sales rep assigned",
                      path: "",
                      url: counter.registrationUrl,
                      audience: "Karigars",
                    }}
                    onToast={toast}
                    onPoster={() => setPosterCounter(counter)}
                  />
                ) : null,
              )}
            </CardBody>
          )}
          {!loading && !error ? <Pagination onChange={setPage} page={page} total={total} totalPages={totalPages} /> : null}
        </Card>
        ) : null}
      </PageBody>
      <Modal
        onClose={() => setPosterCounter(null)}
        open={posterCounter !== null}
        title={`Registration QR${posterCounter?.name ? ` — ${posterCounter.name}` : ""}`}
        width="lg"
      >
        {posterCounter?.registrationUrl ? (
          <QrPosterPanel
            counterName={posterCounter.name}
            counterPhone={posterCounter.phone}
            qrPngUrl={`/api/org/counters/${posterCounter.id}/qr.png`}
            registrationUrl={posterCounter.registrationUrl}
          />
        ) : null}
      </Modal>
    </Page>
  );
}

function LinkItem({
  link,
  onToast,
  onPoster,
}: {
  link: LinkRow;
  onToast: ReturnType<typeof useToast>;
  onPoster?: () => void;
}) {
  const [copied, setCopied] = useState(false);

  // Absolute, because these links are pasted into WhatsApp and email where a
  // relative path means nothing.
  const absolute = link.url ?? `${window.location.origin}${link.path}`;

  const copy = async (): Promise<void> => {
    try {
      if (!(await copyText(absolute))) throw new Error("Copy failed");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onToast.error("Could not copy. Select the link and copy it manually.");
    }
  };

  const share = async (): Promise<void> => {
    if (typeof navigator.share !== "function") {
      void copy();
      return;
    }
    try {
      await navigator.share({ title: link.label, url: absolute });
    } catch (err) {
      // The user dismissing the share sheet is not a failure.
      if ((err as Error)?.name !== "AbortError") void copy();
    }
  };

  return (
    <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-rule p-3.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium text-ink">{link.label}</p>
          <Badge tone="neutral">{link.audience}</Badge>
        </div>
        {link.description ? <p className="mt-0.5 text-sm text-muted">{link.description}</p> : null}
        <p className="mt-2 truncate font-mono text-sm text-brand-ink" title={absolute}>
          {absolute}
        </p>
      </div>

      <div className="flex shrink-0 flex-wrap gap-2">
        <Button
          icon={
            copied ? (
              <Check aria-hidden="true" className="size-4" />
            ) : (
              <Copy aria-hidden="true" className="size-4" />
            )
          }
          onClick={() => void copy()}
          size="sm"
          variant="secondary"
        >
          {copied ? "Copied" : "Copy"}
        </Button>

        <Button
          icon={<Share2 aria-hidden="true" className="size-4" />}
          onClick={() => void share()}
          size="sm"
          variant="secondary"
        >
          Share
        </Button>

        {onPoster ? (
          <Button icon={<QrCode aria-hidden="true" className="size-4" />} onClick={onPoster} size="sm" variant="secondary">
            Design poster
          </Button>
        ) : (
          <a
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-rule bg-raised px-3 font-title text-sm font-semibold text-ink transition-colors hover:border-brand-border hover:bg-brand-light"
            href={link.path}
            rel="noreferrer"
            target="_blank"
          >
            <ExternalLink aria-hidden="true" className="size-4" />
            Open
          </a>
        )}
      </div>
    </div>
  );
}
